import type { RouteLocationRaw } from 'vue-router';

import i18n from '@/i18n';
import { WorkerMissionStatus } from '@/types/mission';

import type {
	FollowingBaseInput,
	FollowingLineGeneratorResult,
	FollowingSegment,
} from '@/components/following/types';

import {
	createLinkSegment,
	createReadonlyLabelSegment,
	createStatusSegment,
	createTextSegment,
} from './segmentFactory';

const STATUS_MARKER_COLORS: Record<WorkerMissionStatus, string> = {
	[WorkerMissionStatus.PROPOSED]: 'var(--yellow-base, #FAB305)',
	[WorkerMissionStatus.REPROPOSED]: 'var(--yellow-base, #FAB305)',
	[WorkerMissionStatus.REPROPOSED_BY_SMS]: 'var(--yellow-base, #FAB305)',
	[WorkerMissionStatus.DECLINED]: 'var(--icon-strong-900, #00061A)',
	[WorkerMissionStatus.NOT_CONFIRMED]: 'var(--icon-strong-900, #00061A)',
	[WorkerMissionStatus.ACCEPTED]: 'var(--green-base, #00A355)',
	[WorkerMissionStatus.CONFIRMED]: 'var(--blue-base, #2051F8)',
	[WorkerMissionStatus.ABORTED]: 'var(--red-base, #E51940)',
	[WorkerMissionStatus.REJECTED_BY_AGENCY]: 'var(--red-base, #E51940)',
};

export interface MissionFollowingInput extends FollowingBaseInput {
	type: WorkerMissionStatus;
	jobTitle: string;
	companyName: string;
	missionTo?: RouteLocationRaw;
	jobTitleTo?: RouteLocationRaw;
	companyNameTo?: RouteLocationRaw;
}

const t = (key: string): string => i18n.global.t(key);

const buildTrailingCompanySegment = (
	input: MissionFollowingInput
): FollowingSegment => {
	if (input.missionTo) {
		return createLinkSegment(input.companyName, { to: input.missionTo });
	}
	if (input.companyNameTo) {
		return createLinkSegment(input.companyName, { to: input.companyNameTo });
	}
	return createReadonlyLabelSegment(input.companyName);
};

const buildSegments = (input: MissionFollowingInput): FollowingSegment[] => {
	const status = createStatusSegment(
		t(`following.labels.mission.${input.type}`),
		STATUS_MARKER_COLORS[input.type]
	);
	const jobTitle = input.jobTitleTo
		? createLinkSegment(input.jobTitle, { to: input.jobTitleTo })
		: createReadonlyLabelSegment(input.jobTitle);
	const company = buildTrailingCompanySegment(input);
	const into = createTextSegment(t('following.labels.mission.connectors.into'));
	const laMission = createTextSegment(
		t('following.labels.mission.connectors.laMission')
	);

	switch (input.type) {
		case WorkerMissionStatus.PROPOSED:
			return [status, jobTitle, into, company];
		case WorkerMissionStatus.DECLINED:
			return [status, laMission, jobTitle, into, company];
		case WorkerMissionStatus.ACCEPTED:
			return [
				status,
				laMission,
				jobTitle,
				createTextSegment(
					t('following.labels.mission.connectors.viaAppMobile')
				),
				company,
			];
		case WorkerMissionStatus.CONFIRMED:
			return [status, jobTitle, into, company];
		case WorkerMissionStatus.ABORTED:
			return [
				status,
				jobTitle,
				createTextSegment(
					t('following.labels.mission.connectors.viaAppMobileDans')
				),
				company,
			];
		case WorkerMissionStatus.REJECTED_BY_AGENCY:
			return [status, jobTitle, into, company];
		case WorkerMissionStatus.REPROPOSED:
			return [
				status,
				jobTitle,
				createTextSegment(t('following.labels.mission.connectors.reproposed')),
				company,
			];
		case WorkerMissionStatus.REPROPOSED_BY_SMS:
			return [
				status,
				jobTitle,
				createTextSegment(
					t('following.labels.mission.connectors.reproposedBySMS')
				),
				company,
			];
		case WorkerMissionStatus.NOT_CONFIRMED:
			return [status, jobTitle, into, company];
	}
};

/**
 * Builds FollowingLine props for a mission event.
 * Covers: proposed, refused (mobile), accepted (mobile), confirmed, and the three cancellation variants
 * (worker via mobile, worker via Wizim CR, agency via Wizim CR).
 */
export const buildFollowingLineMission = (
	input: MissionFollowingInput
): FollowingLineGeneratorResult => {
	return {
		icon: 'file-description',
		segments: buildSegments(input),
		detail: input.detail,
	};
};
